/**
 * The named colours a track or check-in can carry.
 *
 * Names, not hex values. A stored `#3dbe6a` is one colour forever. A stored
 * `leaf` is whatever the current theme says leaf is, so dark mode, print and a
 * future native app can each draw the same choice in the shade that works on
 * their background. It also cannot become CSS injection (see `colour.ts`),
 * because the only values accepted are the six words below.
 *
 * Six is how many hues stay distinguishable to the common forms of colour
 * blindness when lightness differs too. Colour never carries meaning on its
 * own: every series is named in a legend, and a check-in's name sits beside
 * its colour.
 */
export const PALETTE = [
  'leaf',
  'poppy',
  'cornflower',
  'marigold',
  'heather',
  'clay',
] as const

export type PaletteColour = (typeof PALETTE)[number]

export function isPaletteColour(value: unknown): value is PaletteColour {
  return typeof value === 'string' && (PALETTE as readonly string[]).includes(value)
}

/**
 * FNV-1a, 32-bit.
 *
 * Any stable hash would do. What matters is that it is a pure function of the
 * id, so the same thing gets the same colour on this device, the next one, and
 * in a report printed a year from now.
 */
function hash(value: string): number {
  let result = 0x811c9dc5
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index)
    result = Math.imul(result, 0x01000193)
  }
  return result >>> 0
}

/**
 * A colour worked out from an id, for anything nobody has chosen one for.
 *
 * Check-ins always use this; tracks use it until someone picks a colour. It
 * lives in core rather than the app so the screen, the printed report and the
 * native app cannot disagree about what colour something is.
 */
export function paletteColourForId(id: string): PaletteColour {
  return PALETTE[hash(id) % PALETTE.length] ?? 'leaf'
}

/** The colour a track is drawn in: the one chosen, else the one its id gives. */
export function colourOfTrack(track: {
  readonly id: string
  readonly colour?: PaletteColour
}): PaletteColour {
  return track.colour ?? paletteColourForId(track.id)
}
